/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\widgets\QuickActionsWidget.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState, useMemo, useContext, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Settings, ExternalLink, Search, X } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { WidgetContext } from '../components/WidgetWrapper';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useMenuStore } from '../../../store/menu';
import { useI18n } from '../../../hooks/useI18n';
import { getFlatMenuModules, FlatMenuModule } from '../../../config/menuDefinition';
import { useAuthStore } from '../../../store/auth';

// Saved module info (stored in widget config)
interface SavedModule {
    url: string;
    title: string;
    icon: string;
    external?: boolean;
}

interface QuickActionsWidgetProps {
    config?: {
        savedModules?: SavedModule[]; // Complete module data for display
        columnsPerRow?: number; // 1-8 columns per row
    };
    onConfigChange?: (config: Record<string, any>) => void;
    isEditMode?: boolean;
    registerConfigHandler?: (handler: () => void) => void;
}

// Default widget preferences for card styling
const DEFAULT_CARD_COLOR = '#1e293b';
const DEFAULT_BORDER_COLOR = '#334155';

// Default modules to show if none configured
const DEFAULT_MODULES: SavedModule[] = [
    { url: '/pbx/extensions/extensions', title: 'Extensions', icon: 'Phone' },
    { url: '/pbx/calls-routing/gateways', title: 'Trunks (Gateways)', icon: 'Server' },
    { url: '/pbx/call-center/ring-groups', title: 'Ring Groups', icon: 'Users' },
    { url: '/users', title: 'Users', icon: 'UserCog' },
    { url: '/settings/appearance', title: 'Appearance', icon: 'Palette' },
];

// Convert icon name to PascalCase for Lucide (handles 'circle', 'phone-call', etc.)
const toPascalCase = (str: string): string => {
    return str
        .split(/[-_\s]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
};

// Get icon component dynamically
const getIconComponent = (iconName: string | undefined) => {
    if (!iconName) return LucideIcons.FileText;

    // Try exact match first (already PascalCase)
    let IconComponent = (LucideIcons as any)[iconName];
    if (IconComponent) return IconComponent;

    // Try converting to PascalCase (for 'circle', 'phone-call', etc.)
    const pascalName = toPascalCase(iconName);
    IconComponent = (LucideIcons as any)[pascalName];
    if (IconComponent) return IconComponent;

    // Fallback
    return LucideIcons.FileText;
};

export function QuickActionsWidget({ config, onConfigChange, isEditMode, registerConfigHandler }: QuickActionsWidgetProps) {
    const navigate = useNavigate();
    const { t } = useI18n();
    const { user } = useAuthStore();
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [tempSelectedUrls, setTempSelectedUrls] = useState<string[]>([]);
    const [tempColumnsPerRow, setTempColumnsPerRow] = useState(2);

    const widgetPrefs = useContext(WidgetContext);
    const cardColor = widgetPrefs?.cardColor || DEFAULT_CARD_COLOR;
    const borderColor = widgetPrefs?.borderColor || DEFAULT_BORDER_COLOR;

    // Determine tenant type reactively from auth store
    const isMainTenant = (user as any)?.tenant?.slug === 'main' || (user as any)?.tenantSlug === 'main';

    // Get saved modules from config (complete data for display)
    const savedModules = (config?.savedModules && Array.isArray(config.savedModules))
        ? config.savedModules
        : DEFAULT_MODULES;
    const columnsPerRow = config?.columnsPerRow || 2;

    // Column class based on config
    const gridColsClass = {
        1: 'grid-cols-1',
        2: 'grid-cols-2',
        3: 'grid-cols-2 sm:grid-cols-3',
        4: 'grid-cols-2 sm:grid-cols-4',
        5: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5',
        6: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-6',
        7: 'grid-cols-2 sm:grid-cols-4 md:grid-cols-7',
        8: 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-8',
    }[columnsPerRow] || 'grid-cols-2';

    // All modules from the hardcoded menu — computed synchronously, always available.
    // Falls back to the initialized menuStore if the static function returns nothing.
    const { menuItems: storeMenuItems } = useMenuStore();
    const availableModules = useMemo(() => {
        const modules = getFlatMenuModules(isMainTenant);
        if (modules.length > 0) {
            return modules;
        }
        // Fallback: extract level-3 items from the already-loaded menu store
        const storeModules: FlatMenuModule[] = [];
        const collect = (items: typeof storeMenuItems) => {
            for (const item of items) {
                if (item.level === 3 && item.url) {
                    // Find parent (level-2) slug for grouping
                    const parent = storeMenuItems.find(p => p.id === item.parentId);
                    const grandParent = parent ? storeMenuItems.find(g => g.id === parent.parentId) : null;
                    storeModules.push({
                        slug: item.slug || item.uuid,
                        titleKey: item.slug || item.title,
                        icon: item.icon || 'FileText',
                        url: item.url,
                        parentTitleKey: parent?.slug || 'other',
                        grandParentTitleKey: grandParent?.slug || 'other',
                        mainTenantOnly: false,
                    });
                }
                if (item.children) collect(item.children);
            }
        };
        collect(storeMenuItems);
        return storeModules;
    }, [isMainTenant, storeMenuItems]);


    // Filter modules for search in config modal
    const filteredModules = useMemo(() => {
        if (!searchQuery) return availableModules;
        const query = searchQuery.toLowerCase();
        return availableModules.filter(m => {
            const title = t(m.titleKey) || m.titleKey || '';
            const parent = t(m.parentTitleKey) || m.parentTitleKey || '';
            const searchUrl = m.url || '';
            
            return title.toLowerCase().includes(query) ||
                parent.toLowerCase().includes(query) ||
                searchUrl.toLowerCase().includes(query);
        });
    }, [availableModules, searchQuery, t]);

    // Group modules by parent group title (level-2)
    const groupedModules = useMemo(() => {
        const groups: Record<string, { name: string; modules: FlatMenuModule[] }> = {};

        for (const module of filteredModules) {
            const groupKey = module.parentTitleKey || 'other';
            const grandParentTitle = String(t(module.grandParentTitleKey) || module.grandParentTitleKey || '');
            const parentTitle = String(t(module.parentTitleKey) || module.parentTitleKey || '');
            let groupName = parentTitle;
            if (grandParentTitle && grandParentTitle !== parentTitle) {
                groupName = `${grandParentTitle} › ${parentTitle}`;
            }

            if (!groups[groupKey]) {
                groups[groupKey] = { name: groupName, modules: [] };
            }
            groups[groupKey].modules.push(module);
        }

        return groups;
    }, [filteredModules, t]);

    // Handle navigation
    const handleNavigate = (url: string, external?: boolean, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!isEditMode) {
            if (external) {
                let finalUrl = url;
                if (finalUrl.startsWith('/api/') && (user as any)?.tenant?.id) {
                    finalUrl = `${finalUrl}?domainId=${(user as any).tenant.id}`;
                }
                window.open(finalUrl, '_blank', 'noopener,noreferrer');
            } else {
                navigate(url);
                // Explicitly sync sidebar menu state
                useMenuStore.getState().loadMenuForPath(url);
            }
        }
    };

    // Handle opening config modal
    const handleOpenConfig = useCallback(() => {
        setTempSelectedUrls(savedModules.map(m => m.url));
        setTempColumnsPerRow(columnsPerRow);
        setSearchQuery('');
        setShowConfigModal(true);
    }, [savedModules, columnsPerRow]);

    // Register config handler for external trigger
    useEffect(() => {
        if (registerConfigHandler) {
            registerConfigHandler(handleOpenConfig);
        }
    }, [registerConfigHandler, handleOpenConfig]);

    // Toggle module selection
    const handleToggleModule = (url: string) => {
        setTempSelectedUrls(prev => {
            if (prev.includes(url)) {
                return prev.filter(u => u !== url);
            } else {
                return [...prev, url];
            }
        });
    };

    // Handle save config — resolve title and icon from hardcoded menu definition
    const handleSaveConfig = () => {
        if (onConfigChange) {
            const newSavedModules: SavedModule[] = tempSelectedUrls
                .map(url => {
                    // Look up in the hardcoded module list
                    const module = availableModules.find(m => m.url === url);
                    if (module) {
                        return {
                            url: module.url,
                            title: String(t(module.titleKey) || module.titleKey),
                            icon: module.icon || 'FileText',
                            external: module.external,
                        };
                    }
                    // Keep existing saved module data if not found
                    const existing = savedModules.find(m => m.url === url);
                    return existing || { url, title: url.split('/').pop() || 'Module', icon: 'FileText' };
                });

            onConfigChange({
                ...config,
                savedModules: newSavedModules,
                columnsPerRow: tempColumnsPerRow,
            });
        }
        setShowConfigModal(false);
    };

    // Handle removing a module directly
    const handleRemoveModule = (url: string) => {
        if (!onConfigChange) return;

        const newSavedModules = savedModules.filter(m => m.url !== url);
        onConfigChange({
            ...config,
            savedModules: newSavedModules
        });
    };

    return (
        <div className="h-full flex flex-col p-2">
            {/* Actions Grid */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-1">
                {savedModules.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                        <ExternalLink className="w-8 h-8 mb-2 opacity-50" />
                        <p className="text-sm">No quick actions configured</p>
                        {isEditMode && (
                            <button
                                onClick={handleOpenConfig}
                                className="mt-2 text-xs text-primary hover:underline"
                            >
                                Click to configure
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={`grid ${gridColsClass} gap-2`}>
                        {savedModules.map(module => {
                            const IconComponent = getIconComponent(module.icon);
                            return (
                                <div key={module.url} className="relative group/item">
                                    <SimpleTooltip content={isEditMode ? module.title : ""} className="w-full">
                                        <button
                                            type="button"
                                            onClick={(e) => handleNavigate(module.url, module.external, e)}
                                            className={`w-full p-2 rounded-lg text-left transition-all hover:scale-[1.02] hover:shadow-lg group ${isEditMode ? 'cursor-default' : ''}`}
                                            style={{
                                                backgroundColor: cardColor,
                                                border: `1px solid ${borderColor}`
                                            }}
                                            disabled={isEditMode}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 rounded-md bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors flex-shrink-0">
                                                    <IconComponent className="w-5 h-5" />
                                                </div>
                                                <span className="font-medium text-xs text-foreground truncate">
                                                    {module.title}
                                                </span>
                                            </div>
                                        </button>
                                    </SimpleTooltip>

                                    {/* Delete Button in Edit Mode */}
                                    {isEditMode && (
                                        <SimpleTooltip content="Remove action" className="absolute top-1.5 right-1.5 z-10">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveModule(module.url);
                                                }}
                                                className="p-1.5 rounded-full bg-background/80 backdrop-blur-sm border border-border text-muted-foreground hover:text-destructive hover:border-destructive hover:bg-destructive/10 transition-all duration-200 shadow-sm"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </SimpleTooltip>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Config Modal - rendered via Portal to escape widget overflow */}
            {showConfigModal && createPortal(
                <div
                    className="fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 p-4 pt-16 overflow-y-auto"
                    onClick={() => setShowConfigModal(false)}
                >
                    <div
                        className="bg-background border border-border rounded-xl shadow-2xl max-w-2xl w-full my-4 flex flex-col"
                        style={{ maxHeight: 'calc(100vh - 8rem)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between p-4 border-b border-border">
                            <div className="flex items-center gap-2">
                                <Settings className="w-5 h-5 text-primary" />
                                <h3 className="text-lg font-semibold text-foreground">Configure Quick Actions</h3>
                            </div>
                            <button
                                onClick={() => setShowConfigModal(false)}
                                className="p-1 rounded-lg hover:bg-muted transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Search & Options */}
                        <div className="p-4 border-b border-border space-y-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    placeholder="Search modules..."
                                    className="w-full pl-10 pr-4 py-2 bg-muted border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-foreground">Columns per row:</span>
                                <div className="flex gap-1">
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                                        <button
                                            key={num}
                                            onClick={() => setTempColumnsPerRow(num)}
                                            className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${tempColumnsPerRow === num
                                                ? 'bg-primary text-white'
                                                : 'bg-muted text-foreground hover:bg-muted/80'
                                                }`}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Module List */}
                        <div className="flex-1 overflow-auto p-4 space-y-4">
                            {Object.keys(groupedModules).length === 0 ? (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p>No modules found</p>
                                </div>
                            ) : (
                                Object.entries(groupedModules).map(([groupKey, group]) => (
                                    <div key={groupKey}>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-2">
                                            {group.name}
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {group.modules.map(module => {
                                                const isSelected = tempSelectedUrls.includes(module.url);
                                                const IconComponent = getIconComponent(module.icon);
                                                return (
                                                    <button
                                                        key={module.slug}
                                                        onClick={() => handleToggleModule(module.url)}
                                                        className={`p-3 rounded-lg text-left transition-all ${
                                                            isSelected
                                                                ? 'bg-primary/20 border-primary ring-1 ring-primary'
                                                                : 'bg-muted hover:bg-muted/80 border-transparent'
                                                        } border`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary text-white' : 'bg-background'}`}>
                                                                <IconComponent className="w-4 h-4" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-sm truncate">{String(t(module.titleKey) || module.titleKey)}</p>
                                                                <p className="text-xs text-muted-foreground truncate">{module.url}</p>
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-between p-4 border-t border-border">
                            <span className="text-sm text-muted-foreground">
                                {tempSelectedUrls.length} modules selected
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowConfigModal(false)}
                                    className="px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveConfig}
                                    className="px-4 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors text-sm"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

